// @mui
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
// components
import Iconify from 'src/components/iconify';
// hooks
import useBrandName from 'src/hooks/use-brand-name';

// ----------------------------------------------------------------------

export default function MaintenanceBanner() {
  const { isAgent } = useBrandName();

  const logoPath = isAgent ? '/logo/logo_agent_single.svg' : '/logo/logo.svg';
  const logoAlt = isAgent ? 'TryVisa Agent Logo' : 'TryVisa Logo';

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        width: '100%',
        height: '100vh',
        background: (theme) =>
          theme.palette.mode === 'light'
            ? 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
            : 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <Container maxWidth="sm">
        <Card
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 3,
            boxShadow: (theme) =>
              theme.palette.mode === 'light'
                ? '0 20px 60px rgba(0, 0, 0, 0.1)'
                : '0 20px 60px rgba(0, 0, 0, 0.3)',
            textAlign: 'center',
          }}
        >
          <Stack spacing={4} alignItems="center">
            <Box
              component="img"
              src={logoPath}
              alt={logoAlt}
              sx={{
                width: { xs: 150, md: 200 },
                height: 'auto',
              }}
            />

            <Stack spacing={2} alignItems="center">
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: { xs: '1.75rem', md: '2.5rem' },
                }}
              >
                We will be back soon
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  maxWidth: 500,
                  lineHeight: 1.7,
                }}
              >
                We are temporarily unavailable. We&apos;re working hard to bring you back online as
                soon as possible.
              </Typography>
            </Stack>

            <Divider sx={{ width: '100%', my: 2 }} />

            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: (theme) =>
                  theme.palette.mode === 'light' ? 'primary.lighter' : 'primary.darker',
                width: '100%',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'primary.main',
                  mb: 2,
                }}
              >
                Open for Business Collaborations
              </Typography>
              <Stack spacing={2} alignItems="flex-start" sx={{ pl: { xs: 0, sm: 2 } }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Iconify
                    icon="solar:phone-bold"
                    width={24}
                    sx={{ color: 'primary.main', flexShrink: 0 }}
                  />
                  <Link
                    href="tel:+918302453029"
                    sx={{
                      color: 'text.primary',
                      textDecoration: 'none',
                      fontSize: '1rem',
                      fontWeight: 500,
                      '&:hover': {
                        color: 'primary.main',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    +918302453029
                  </Link>
                </Stack>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Iconify
                    icon="solar:letter-bold"
                    width={24}
                    sx={{ color: 'primary.main', flexShrink: 0 }}
                  />
                  <Link
                    href="mailto:deepakm@flyzygo.com"
                    sx={{
                      color: 'text.primary',
                      textDecoration: 'none',
                      fontSize: '1rem',
                      fontWeight: 500,
                      '&:hover': {
                        color: 'primary.main',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    deepakm@flyzygo.com
                  </Link>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </Card>
      </Container>
    </Box>
  );
}
